// Push notifications in the browser (push.js on the server, public/sw.js in the browser).
// Turned on per device from Settings; each kind can be switched off there too.
import { api } from './api';

export const pushSupported = () => typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
// iPhones and iPads only allow push for sites added to the home screen.
export const needsHomeScreen = () => /iP(hone|ad|od)/.test(navigator.userAgent) && !window.matchMedia('(display-mode: standalone)').matches;

function keyBytes(base64) {
  const padded = (base64 + '='.repeat((4 - (base64.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/');
  return Uint8Array.from(atob(padded), (c) => c.charCodeAt(0));
}

async function registration() {
  return navigator.serviceWorker.register('/sw.js');
}

// This device's subscription, or null.
export async function currentSubscription() {
  if (!pushSupported()) return null;
  const reg = await navigator.serviceWorker.getRegistration('/sw.js');
  return reg ? reg.pushManager.getSubscription() : null;
}

// Asks for permission (on a click) and saves this device. Returns 'on', 'denied' or 'unsupported'.
export async function turnOnPush() {
  if (!pushSupported()) return 'unsupported';
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return 'denied';
  const reg = await registration();
  await navigator.serviceWorker.ready;
  const { publicKey } = await api('/push/key');
  const sub = (await reg.pushManager.getSubscription()) || await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(publicKey) });
  await api('/push/subscribe', { method: 'POST', body: { subscription: sub.toJSON() } });
  return 'on';
}

export async function turnOffPush() {
  const sub = await currentSubscription();
  if (!sub) return;
  await api('/push/unsubscribe', { method: 'POST', body: { endpoint: sub.endpoint } }).catch(() => {});
  await sub.unsubscribe().catch(() => {});
}

// Whether this device is on, checked with the server too (a device can be removed elsewhere).
export async function pushStatus() {
  if (!pushSupported()) return { supported: false };
  const sub = await currentSubscription();
  const status = await api('/push/status', { method: 'POST', body: { endpoint: sub ? sub.endpoint : null } }).catch(() => ({ thisDevice: false, devices: 0 }));
  return { supported: true, permission: Notification.permission, ...status };
}

// Market event alerts live in the browser; a copy goes to the server so pushes can ring
// with the site closed. Sent a moment after the last change.
let syncTimer = null;
export function syncMarketAlerts(alerts) {
  clearTimeout(syncTimer);
  syncTimer = setTimeout(() => {
    const events = Object.fromEntries(Object.entries(alerts.events || {}).filter(([, m]) => m));
    api('/push/market-alerts', { method: 'PUT', body: { auto: alerts.auto, events } }).catch(() => {});
  }, 1500);
}
